<template>
  <div class="character-manager">
    <div class="manager-header">
      <h3>{{ $t('characterManager.title') }}</h3>
      <div class="tabs">
        <button 
          :class="{ active: activeTab === 'local' }" 
          @click="activeTab = 'local'"
        >
          {{ $t('characterManager.myRoles') }}
        </button>
        <button 
          :class="{ active: activeTab === 'store' }" 
          @click="loadStore"
        >
          {{ $t('characterManager.onlineStore') }}
        </button>
      </div>
      <button class="close-btn" @click="$emit('close')">
        <svg viewBox="0 0 24 24" width="24" height="24"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
      </button>
    </div>

    <div class="manager-content">
      <!-- Local Characters -->
      <div v-if="activeTab === 'local'" class="local-tab">
        <div class="actions-bar">
          <button class="btn-primary" @click="startCreate">
            <span>{{ $t('characterManager.createRole') }}</span>
          </button>
          <button class="btn-secondary" @click="triggerImport">
            <span>{{ $t('characterManager.import') }}</span>
          </button>
          <input type="file" ref="importInput" @change="handleImport" style="display: none" accept=".json">
        </div>

        <div class="roles-container">
          <!-- Custom Roles (Downloaded/Created) -->
          <div v-if="customRoles.length > 0" class="role-section">
            <div class="section-title">{{ $t('characterManager.myDownloads') }}</div>
            
            <div v-for="(group, groupName) in customRolesByTag" :key="groupName" class="tag-group">
              <div class="group-label" v-if="groupName !== $t('characterManager.uncategorized')">{{ groupName }}</div>
              <div class="cards-grid">
                <div 
                  v-for="role in group" 
                  :key="role.id" 
                  class="character-card custom"
                  :class="{ active: currentRoleId === role.id }"
                  @click="selectRole(role)"
                >
                  <div class="card-avatar">{{ role.icon }}</div>
                  <div class="card-info">
                    <div class="card-name">{{ role.name }}</div>
                    <div class="card-desc" :title="role.description">{{ role.description }}</div>
                    <div class="card-tags" v-if="role.tags && role.tags.length">
                      <span v-for="tag in role.tags.slice(0, 2)" :key="tag" class="tag-pill">{{ tag }}</span>
                    </div>
                  </div>
                  <div class="card-actions">
                    <button class="icon-btn-small" @click.stop="shareRole(role)" :title="$t('characterManager.shareToStore')">
                      <svg viewBox="0 0 24 24" width="14" height="14"><circle cx="18" cy="5" r="3"></circle><circle cx="6" cy="12" r="3"></circle><circle cx="18" cy="19" r="3"></circle><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line></svg>
                    </button>
                    <button class="icon-btn-small" @click.stop="exportRole(role)" :title="$t('characterManager.exportJson')">
                      <svg viewBox="0 0 24 24" width="14" height="14"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                    </button>
                    <button class="icon-btn-small" @click.stop="startEdit(role)" :title="$t('characterManager.edit')">
                      <svg viewBox="0 0 24 24" width="14" height="14"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                    </button>
                    <button class="icon-btn-small delete-btn" @click.stop="deleteRole(role.id)" :title="$t('characterManager.delete')">
                      <svg viewBox="0 0 24 24" width="14" height="14"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Default Roles -->
          <div class="role-section">
            <div class="section-title">{{ $t('characterManager.systemDefault') }}</div>
            <div class="cards-grid">
              <div 
                v-for="role in visibleDefaultRoles" 
                :key="role.id" 
                class="character-card"
                :class="{ active: currentRoleId === role.id }"
                @click="selectRole(role)"
              >
                <div class="card-avatar">{{ role.icon }}</div>
                <div class="card-info">
                  <div class="card-name">{{ getRoleName(role) }}</div>
                  <div class="card-desc" :title="getRoleDescription(role)">{{ getRoleDescription(role) }}</div>
                </div>
                <div class="card-actions">
                   <button class="icon-btn-small" @click.stop="toggleDefaultVisibility(role.id)" :title="$t('characterManager.hide')">
                     <svg viewBox="0 0 24 24" width="14" height="14"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                   </button>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Hidden Roles Toggle -->
          <div v-if="hiddenRolesCount > 0" class="show-hidden-btn" @click="showHidden = !showHidden">
             {{ showHidden ? $t('characterManager.hideBlockedRoles') : $t('characterManager.showHiddenRoles', { count: hiddenRolesCount }) }}
          </div>
          
          <div v-if="showHidden" class="role-section">
             <div class="cards-grid">
               <div 
                  v-for="role in hiddenDefaultRoles" 
                  :key="role.id" 
                  class="character-card hidden-role"
                >
                  <div class="card-avatar">{{ role.icon }}</div>
                  <div class="card-info">
                    <div class="card-name">{{ getRoleName(role) }}</div>
                  </div>
                  <div class="card-actions">
                     <button class="icon-btn-small" @click.stop="toggleDefaultVisibility(role.id)" :title="$t('characterManager.restoreVisibility')">
                       <svg viewBox="0 0 24 24" width="14" height="14"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8-11-8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                     </button>
                  </div>
                </div>
             </div>
          </div>

        </div>
      </div>

      <!-- Online Store -->
      <div v-else class="store-tab">
        <div v-if="storeLoading" class="loading-state">
          <svg class="spinner" viewBox="0 0 50 50"><circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="5"></circle></svg>
          <span>{{ $t('characterManager.loadingStore') }}</span>
        </div>
        <div v-else class="cards-grid">
           <div v-for="card in storeCards" :key="card.id" class="character-card store-card">
              <div class="card-avatar">{{ card.avatar }}</div>
              <div class="card-info">
                <div class="card-name">{{ card.name }}</div>
                <div class="card-desc">{{ card.description }}</div>
                <div class="store-meta">
                  <span class="meta-item">@{{ card.author }}</span>
                  <span class="meta-item">{{ formatDate(card.date) }}</span>
                </div>
               </div>
               <div class="card-actions-store">
                <button class="icon-btn-small download-btn" @click="downloadCard(card)" :title="$t('characterManager.downloadRole')">
                  <svg viewBox="0 0 24 24" width="16" height="16"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                </button>
              </div>
           </div>
           <div v-if="storeCards.length === 0" class="empty-store">
             {{ $t('characterManager.emptyStore') }}
            </div>
        </div>
      </div>
    </div>

    <!-- Edit/Create Modal -->
    <div v-if="isEditing" class="edit-modal-overlay">
      <div class="edit-modal">
        <div class="modal-header">
          <h3>{{ editForm.id ? $t('characterManager.editRole') : $t('characterManager.createNewRole') }}</h3>
          <button class="close-btn" @click="cancelEdit">×</button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <div class="form-group avatar-group">
              <label>{{ $t('characterManager.avatarLabel') }}</label>
              <input v-model="editForm.icon" class="icon-input" maxlength="2" placeholder="🤖">
            </div>
            <div class="form-group name-group">
              <label>{{ $t('characterManager.nameLabel') }}</label>
              <input v-model="editForm.name" :placeholder="$t('characterManager.namePlaceholder')">
            </div>
          </div>
          <div class="form-group">
            <label>{{ $t('characterManager.shortDescriptionLabel') }}</label>
            <input v-model="editForm.description" :placeholder="$t('characterManager.descriptionPlaceholder')">
          </div>
          <div class="form-group">
            <label>{{ $t('characterManager.aliasesLabel') }}</label>
            <input v-model="editForm.aliasesInput" :placeholder="$t('characterManager.aliasesPlaceholder')">
          </div>
          <div class="form-group">
            <label>{{ $t('characterManager.systemPromptLabel') }}</label>
            <textarea v-model="editForm.systemPrompt" rows="6" :placeholder="$t('characterManager.systemPromptPlaceholder')"></textarea>
          </div>
          <div class="form-group">
            <label>{{ $t('characterManager.tagsLabel') }}</label>
            <input v-model="editForm.tagsInput" :placeholder="$t('characterManager.tagsPlaceholder')">
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="cancelEdit">{{ $t('characterManager.cancel') }}</button>
          <button class="btn-primary" @click="saveEdit">{{ $t('characterManager.save') }}</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { store, mutations, defaultRoles } from '../store';
import { saveAs } from 'file-saver';

export default {
  name: 'CharacterManager',
  data() {
    return {
      activeTab: 'local',
      isEditing: false,
      editForm: {
        id: null,
        name: '',
        icon: '',
        description: '',
        systemPrompt: '',
        tagsInput: '',
        aliasesInput: ''
        ,aliasesInput: ''
      },
      showHidden: false
    };
  },
  computed: {
    currentRoleId() {
      return (store.companionSettings && store.companionSettings.activeCompanionIds || [])[0] || 'general';
    },
    visibleDefaultRoles() {
      const deletedIds = store.companionSettings && store.companionSettings.hiddenDefaultCompanions || store.roleSettings.deletedDefaultRoles || [];
      return defaultRoles.filter(r => !deletedIds.includes(r.id));
    },
    hiddenDefaultRoles() {
      const deletedIds = store.companionSettings && store.companionSettings.hiddenDefaultCompanions || store.roleSettings.deletedDefaultRoles || [];
      return defaultRoles.filter(r => deletedIds.includes(r.id));
    },
    hiddenRolesCount() {
        return (store.companionSettings && store.companionSettings.hiddenDefaultCompanions || store.roleSettings.deletedDefaultRoles || []).length;
    },
    customRoles() {
      return store.companionSettings && store.companionSettings.customCompanions || store.roleSettings.customRoles || [];
    },
    storeCards() {
      return store.onlineStore.cards;
    },
    storeLoading() {
      return store.onlineStore.loading;
    },
    customRolesByTag() {
      const roles = this.customRoles;
      const groups = {};
      
      roles.forEach(role => {
        // Use first tag as category, or localized "Uncategorized"
        const tag = (role.tags && role.tags.length > 0) ? role.tags[0] : this.$t('characterManager.uncategorized');
        if (!groups[tag]) {
          groups[tag] = [];
        }
        groups[tag].push(role);
      });
      
      return groups;
    }
  },
  methods: {
    getRoleName(role) {
      if (!role) return '';
      return role.isDefault ? this.$tl(role.name) : role.name;
    },
    getRoleDescription(role) {
      if (!role) return '';
      return role.isDefault ? this.$tl(role.description) : role.description;
    },
    selectRole(role) {
      this.$emit('select-companion', role);
      mutations.setActiveRole(role.id);
      this.$emit('close');
    },
    toggleDefaultVisibility(id) {
      mutations.toggleDefaultRole(id);
    },
    startCreate() {
      this.editForm = {
        id: null,
        name: '',
        icon: '',
        description: '',
        systemPrompt: '',
        tagsInput: '',
        aliasesInput: ''
      };
      this.isEditing = true;
    },
    startEdit(role) {
      this.editForm = {
        ...role,
        tagsInput: role.tags ? role.tags.join(', ') : '',
        aliasesInput: role.aliases ? role.aliases.join(', ') : ''
      };
      this.isEditing = true;
    },
    cancelEdit() {
      this.isEditing = false;
    },
    saveEdit() {
      const roleData = {
        name: this.editForm.name,
        icon: this.editForm.icon || '👤',
        description: this.editForm.description,
        systemPrompt: this.editForm.systemPrompt,
        tags: this.editForm.tagsInput.split(/[,，]/).map(t => t.trim()).filter(t => t),
        aliases: this.editForm.aliasesInput.split(/[,，]/).map(t => t.trim()).filter(t => t)
      };

      if (this.editForm.id) {
        // Update
        mutations.updateCustomRole({
          id: this.editForm.id,
          ...roleData
        });
      } else {
        // Create
        mutations.addCustomRole(roleData);
      }
      this.isEditing = false;
    },
    async deleteRole(id) {
      if (await confirm(this.$t('characterManager.deleteConfirm'))) {
        mutations.deleteCustomRole(id);
      }
    },
    exportRole(role) {
      const blob = new Blob([JSON.stringify(role, null, 2)], { type: 'application/json' });
      saveAs(blob, `${role.name}_character_card.json`);
    },
    triggerImport() {
      this.$refs.importInput.click();
    },
    handleImport(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const card = JSON.parse(e.target.result);
          mutations.importCharacterCard(card);
          alert(this.$t('characterManager.importSuccess'));
        } catch (err) {
          alert(this.$t('characterManager.importFailed'));
        }
      };
      reader.readAsText(file);
    },
    async loadStore() {
      this.activeTab = 'store';
      await mutations.fetchOnlineCards();
    },
    async shareRole(role) {
        if (!await confirm(this.$t('characterManager.shareConfirm'))) return;
        
        const result = await mutations.shareCard(role);
        if (result.success) {
            alert(this.$t('characterManager.shareSuccess'));
        } else {
            alert(this.$t('characterManager.shareFailed', {
              message: result.error || this.$t('characterManager.unknownError')
            }));
        }
    },
    downloadCard(card) {
        const existingRoles = store.roleSettings.customRoles || [];
        const isDuplicate = existingRoles.some(r => r.name === card.name);
        
        mutations.importCharacterCard(card);
        
        if (isDuplicate) {
          alert(this.$t('characterManager.duplicateDownloaded', { name: card.name }));
        } else {
          alert(this.$t('characterManager.downloaded'));
        }
    },
    formatDate(dateStr) {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleDateString();
    }
  }
};
</script>

<style scoped>
.character-manager {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  --cm-bg: var(--bg-color, var(--bg-main, #ffffff));
  --cm-surface: var(--card-bg, var(--bg-surface, #ffffff));
  --cm-surface-elevated: var(--bg-color, var(--bg-surface, #ffffff));
  --cm-input-bg: var(--input-bg, var(--bg-input, #ffffff));
  --cm-text: var(--text-color, var(--text-primary, #333333));
  --cm-text-muted: var(--text-color-secondary, var(--text-secondary, #666666));
  --cm-border: var(--border-color, rgba(0, 0, 0, 0.08));
  --cm-hover-bg: rgba(0, 0, 0, 0.05);
  --cm-tag-bg: var(--btn-secondary-bg, #f0f0f0);
  --cm-muted-bg: rgba(0, 0, 0, 0.03);
  --cm-card-shadow: 0 1px 3px rgba(0,0,0,0.05);
  --cm-card-hover-shadow: 0 10px 24px rgba(0,0,0,0.12), 0 0 0 1px var(--primary-color, #4a90e2) inset;
  --cm-active-bg: var(--primary-color-light, #eef6ff);
  --cm-active-shadow: 0 8px 22px rgba(74, 144, 226, 0.18);
  --cm-modal-overlay: rgba(0,0,0,0.5);
  --cm-modal-shadow: 0 18px 48px rgba(0,0,0,0.22);
  --cm-ease: cubic-bezier(0.22, 1, 0.36, 1);
  background: var(--cm-bg);
  color: var(--cm-text);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  animation: cm-fade-in 180ms var(--cm-ease);
}

.manager-header {
  padding: 16px 24px;
  border-bottom: 1px solid var(--cm-border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--cm-surface-elevated);
}

@media (max-width: 768px) {
  .manager-header {
    padding-top: calc(16px + 65px);
  }
}

.manager-header h3 {
  margin: 0;
  font-size: 18px;
}

.tabs {
  display: flex;
  gap: 12px;
}

.tabs button {
  background: none;
  border: none;
  padding: 8px 16px;
  cursor: pointer;
  border-radius: 20px;
  font-weight: 500;
  color: var(--cm-text-muted);
  transition: background-color 180ms var(--cm-ease), color 180ms var(--cm-ease), transform 180ms var(--cm-ease), box-shadow 180ms var(--cm-ease);
}

.tabs button:hover {
  background: var(--cm-hover-bg);
  color: var(--cm-text);
  transform: translateY(-1px);
}

.tabs button.active {
  background: var(--primary-color, #4a90e2);
  color: white;
  box-shadow: 0 8px 18px rgba(74, 144, 226, 0.22);
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: var(--cm-text);
  border-radius: 8px;
  transition: background-color 180ms var(--cm-ease), color 180ms var(--cm-ease), transform 180ms var(--cm-ease);
}

.close-btn:hover {
  background: var(--cm-hover-bg);
  transform: rotate(90deg);
}

.manager-content {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}

.actions-bar {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
}

.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
}

.character-card {
  border: 1px solid var(--cm-border);
  border-radius: 10px;
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: transform 220ms var(--cm-ease), box-shadow 220ms var(--cm-ease), border-color 220ms var(--cm-ease), background-color 220ms var(--cm-ease), opacity 180ms var(--cm-ease);
  background: var(--cm-surface);
  position: relative;
  box-shadow: var(--cm-card-shadow);
  animation: cm-card-enter 240ms var(--cm-ease) both;
}

.character-card:hover {
  transform: translateY(-3px);
  box-shadow: var(--cm-card-hover-shadow);
}

.character-card.active {
  border-color: var(--primary-color, #4a90e2);
  background: var(--cm-active-bg);
  box-shadow: var(--cm-active-shadow);
}

.card-avatar {
  font-size: 24px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--cm-muted-bg);
  border-radius: 8px;
  box-shadow: inset 0 1px 2px rgba(0,0,0,0.05);
  transition: transform 220ms var(--cm-ease), background-color 220ms var(--cm-ease);
}

.character-card:hover .card-avatar {
  transform: scale(1.06) rotate(-2deg);
}

.card-info {
  flex: 1;
  overflow: hidden;
}

.card-name {
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--cm-text);
}

.card-desc {
  font-size: 11px;
  color: var(--cm-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-actions {
  display: flex;
  gap: 2px;
  opacity: 0;
  transition: opacity 180ms var(--cm-ease), transform 180ms var(--cm-ease);
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translate(4px, -50%);
  background: var(--cm-surface);
  padding-left: 8px;
  border-radius: 4px;
  box-shadow: -8px 0 12px var(--cm-surface);
}

.character-card:hover .card-actions {
  opacity: 1;
  transform: translate(0, -50%);
}

.icon-btn-small {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: var(--cm-text-muted);
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-btn-small:hover {
  background: var(--cm-hover-bg);
  color: var(--primary-color, #4a90e2);
  transform: translateY(-1px) scale(1.06);
}

.icon-btn-small.delete-btn:hover {
  color: #ff4d4f;
  background: rgba(255, 77, 79, 0.1);
}

.section-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--cm-text-muted);
  margin: 20px 0 10px;
  padding-bottom: 6px;
  border-bottom: 1px solid var(--cm-border);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.role-section:first-child .section-title {
  margin-top: 0;
}

.tag-group {
  margin-bottom: 16px;
}

.group-label {
  font-size: 12px;
  color: var(--cm-text-muted);
  margin-bottom: 6px;
  padding-left: 4px;
  font-weight: 600;
}

.card-tags {
  display: flex;
  gap: 4px;
  margin-top: 3px;
}

.tag-pill {
  font-size: 9px;
  background: var(--cm-tag-bg);
  padding: 1px 5px;
  border-radius: 3px;
  color: var(--cm-text-muted);
  border: 1px solid var(--cm-border);
}

.show-hidden-btn {
  grid-column: 1 / -1;
  text-align: center;
  padding: 12px;
  color: var(--cm-text-muted);
  cursor: pointer;
  font-size: 13px;
  border-radius: 8px;
  transition: background-color 180ms var(--cm-ease), color 180ms var(--cm-ease), transform 180ms var(--cm-ease);
}

.show-hidden-btn:hover {
  background: var(--cm-hover-bg);
  color: var(--primary-color, #4a90e2);
  transform: translateY(-1px);
}

.hidden-role {
  opacity: 0.6;
  border-style: dashed;
}

/* Edit Modal */
.edit-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--cm-modal-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(4px);
  animation: cm-fade-in 160ms var(--cm-ease);
}

.edit-modal {
  background: var(--cm-surface-elevated);
  width: 500px;
  max-width: 90%;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  color: var(--cm-text);
  box-shadow: var(--cm-modal-shadow);
  animation: cm-modal-enter 220ms var(--cm-ease);
}

.modal-header {
  padding: 16px 24px;
  border-bottom: 1px solid var(--cm-border);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  max-height: 70vh;
}

.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid var(--cm-border);
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 500;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid var(--cm-border);
  border-radius: 6px;
  background: var(--cm-input-bg);
  color: var(--cm-text);
  transition: border-color 180ms var(--cm-ease), box-shadow 180ms var(--cm-ease), background-color 180ms var(--cm-ease);
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: var(--primary-color, #4a90e2);
  box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.16);
}

.form-row {
  display: flex;
  gap: 16px;
}

.avatar-group {
  width: 80px;
}

.name-group {
  flex: 1;
}

.icon-input {
  text-align: center;
  font-size: 20px;
}

.btn-primary {
  background: var(--primary-color, #4a90e2);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: opacity 180ms var(--cm-ease), transform 180ms var(--cm-ease), box-shadow 180ms var(--cm-ease);
}

.btn-secondary {
  background: var(--btn-secondary-bg, var(--cm-muted-bg));
  color: var(--cm-text);
  border: 1px solid var(--cm-border);
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 180ms var(--cm-ease), transform 180ms var(--cm-ease), border-color 180ms var(--cm-ease);
}

.btn-primary:hover {
  opacity: 0.9;
  transform: translateY(-1px);
  box-shadow: 0 8px 18px rgba(74, 144, 226, 0.2);
}

.btn-secondary:hover {
  background: var(--btn-secondary-hover, var(--cm-hover-bg));
  transform: translateY(-1px);
}

/* Store Cards */
.store-card {
  display: flex; /* Changed from block to flex to match local cards */
  padding: 12px;
  gap: 12px;
  align-items: center;
}

.store-meta {
  display: flex;
  gap: 8px;
  margin-top: 4px;
}

.meta-item {
  font-size: 10px;
  color: var(--cm-text-muted);
  background: var(--cm-muted-bg);
  padding: 1px 4px;
  border-radius: 3px;
}

.card-actions-store {
  display: flex;
  align-items: center;
}

.download-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--btn-secondary-bg, var(--cm-muted-bg));
  color: var(--primary-color, #4a90e2);
  transition: background-color 180ms var(--cm-ease), color 180ms var(--cm-ease), transform 180ms var(--cm-ease), box-shadow 180ms var(--cm-ease);
}

.download-btn:hover {
  background: var(--primary-color, #4a90e2);
  color: white;
  transform: translateY(-1px) scale(1.1);
  box-shadow: 0 8px 18px rgba(74, 144, 226, 0.24);
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: var(--cm-text-muted);
  gap: 12px;
}

.empty-store {
  grid-column: 1 / -1;
  text-align: center;
  padding: 40px;
  color: var(--cm-text-muted);
}

.spinner {
  width: 30px;
  height: 30px;
  animation: rotate 2s linear infinite;
}

.spinner .path {
  stroke: var(--primary-color, #4a90e2);
  stroke-linecap: round;
  animation: dash 1.5s ease-in-out infinite;
}

@keyframes rotate {
  100% { transform: rotate(360deg); }
}

@keyframes dash {
  0% { stroke-dasharray: 1, 150; stroke-dashoffset: 0; }
  50% { stroke-dasharray: 90, 150; stroke-dashoffset: -35; }
  100% { stroke-dasharray: 90, 150; stroke-dashoffset: -124; }
}

@keyframes cm-fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes cm-card-enter {
  from {
    opacity: 0;
    transform: translateY(6px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes cm-modal-enter {
  from {
    opacity: 0;
    transform: translateY(10px) scale(0.97);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .character-manager,
  .character-card,
  .edit-modal-overlay,
  .edit-modal,
  .spinner,
  .spinner .path {
    animation: none;
  }

  .tabs button,
  .close-btn,
  .character-card,
  .card-avatar,
  .card-actions,
  .icon-btn-small,
  .show-hidden-btn,
  .form-group input,
  .form-group textarea,
  .btn-primary,
  .btn-secondary,
  .download-btn {
    transition: none;
  }
}
</style>

<style>
[data-theme="dark"] .character-manager {
  --cm-bg: var(--bg-main, #0b0d12);
  --cm-surface: rgba(28, 28, 30, 0.82);
  --cm-surface-elevated: rgba(22, 25, 37, 0.96);
  --cm-input-bg: rgba(255, 255, 255, 0.06);
  --cm-text: var(--text-primary, #f8fafc);
  --cm-text-muted: var(--text-secondary, #a1a1aa);
  --cm-border: rgba(255, 255, 255, 0.09);
  --cm-hover-bg: rgba(255, 255, 255, 0.08);
  --cm-tag-bg: rgba(255, 255, 255, 0.07);
  --cm-muted-bg: rgba(255, 255, 255, 0.08);
  --cm-card-shadow: 0 10px 28px rgba(0, 0, 0, 0.24);
  --cm-card-hover-shadow: 0 16px 36px rgba(0, 0, 0, 0.34), 0 0 0 1px rgba(10, 132, 255, 0.65) inset;
  --cm-active-bg: rgba(10, 132, 255, 0.16);
  --cm-active-shadow: 0 14px 34px rgba(10, 132, 255, 0.18);
  --cm-modal-overlay: rgba(0, 0, 0, 0.64);
  --cm-modal-shadow: 0 24px 64px rgba(0, 0, 0, 0.5);
}

[data-theme="dark"] .character-manager .manager-header,
[data-theme="dark"] .character-manager .edit-modal {
  backdrop-filter: blur(16px);
}
</style>
